import React, { FC, useState } from 'react';

// eslint-disable-next-line no-restricted-syntax
import { gql, useQuery } from '@apollo/client';
import { Trans, useTranslation } from '@modernloop/i18n';
import { Dialog } from '@modernloop/shared/components';
import { useFlag } from '@modernloop/shared/feature-flag';
import { Link, Stack, Typography } from '@mui/material';

import { AtsType, Permission } from 'src/generated/mloop-graphql';

import DoNotShowAgainKey from 'src/components/Dialog/ConfirmationDialog/DoNotShowAgainKey';
import useDoNotShowAgain from 'src/components/Dialog/ConfirmationDialog/useDoNotShowAgain';

import HelpCenterUrl from 'src/constants/HelpCenterUrl';

import { useHasUserPermission } from 'src/hooks/api/user/get';
import { getAtsService, useOrgAtsService } from 'src/hooks/atsService';
import useHistory from 'src/hooks/useHistory';

const GREENHOUSE_OAUTH_STATUS_QUERY = gql`
  query GreenhouseHarvestV3DialogOAuthStatus {
    thisOrg {
      id
      integrations {
        id
        greenhouseOauth {
          subdomain
        }
      }
    }
  }
`;

const GreenhouseHarvestV3Dialog: FC = () => {
  const { t } = useTranslation('hiring-dashboard-common');
  const history = useHistory();
  const [isOpen, setIsOpen] = useState(true);

  const showGreenhouseV3UiPrompts = useFlag('show_greenhouse_v3_ui_prompts');
  const canManageOrgSettings = useHasUserPermission(Permission.CanManageOrgSettings);
  const canSchedule = useHasUserPermission(Permission.CanSchedule);
  const currentAtsService = useOrgAtsService();
  const isGreenhouseOrg = currentAtsService === getAtsService(AtsType.Greenhouse);
  const { isDoNotShowAgainLoading, doNotShowAgain, addToDoNotShowAgain } = useDoNotShowAgain(
    DoNotShowAgainKey.GREENHOUSE_HARVEST_V3_DIALOG
  );

  const {
    data: greenhouseOAuthData,
    loading: greenhouseOAuthLoading,
    error: greenhouseOAuthError,
  } = useQuery(GREENHOUSE_OAUTH_STATUS_QUERY, {
    skip: !showGreenhouseV3UiPrompts || !isGreenhouseOrg || isDoNotShowAgainLoading || doNotShowAgain || !isOpen,
  });
  const hasGreenhouseOAuthConnection = Boolean(greenhouseOAuthData?.thisOrg?.integrations?.greenhouseOauth);
  if (
    !isOpen ||
    !showGreenhouseV3UiPrompts ||
    !isGreenhouseOrg ||
    !canSchedule ||
    hasGreenhouseOAuthConnection ||
    isDoNotShowAgainLoading ||
    doNotShowAgain ||
    greenhouseOAuthLoading ||
    greenhouseOAuthError
  ) {
    return null;
  }

  const persistDoNotShowAgain = () => {
    addToDoNotShowAgain().catch(() => {
      // Ignore persistence failures so they do not block dismissal/navigation.
    });
  };

  const handleDismiss = () => {
    setIsOpen(false);
    persistDoNotShowAgain();
  };

  const handleGoToIntegrations = async () => {
    await addToDoNotShowAgain().catch(() => {});
    setIsOpen(false);
    if (canManageOrgSettings) {
      history.push('/settings/organization/integrations');
    } else {
      history.push('/settings/account/apps');
    }
  };

  const descriptionKey = canManageOrgSettings
    ? 'hiring-dashboard-common:common.greenhouseHarvestV3Dialog.adminDescription'
    : 'hiring-dashboard-common:common.greenhouseHarvestV3Dialog.schedulerDescription';

  return (
    <Dialog
      open={isOpen}
      onClose={handleDismiss}
      decoration={
        <img
          style={{ width: '64px', height: '64px' }}
          src="/static/images/integrations/greenhouse.png"
          alt="Greenhouse"
        />
      }
      title={t('common.greenhouseHarvestV3Dialog.title')}
      maxWidth="sm"
      submitOptions={{
        label: t('common.greenhouseHarvestV3Dialog.goToIntegrations'),
        onClick: handleGoToIntegrations,
      }}
      cancelOptions={{
        label: t('common.greenhouseHarvestV3Dialog.dismiss'),
        onClick: handleDismiss,
        dataTestId: 'greenhouse-harvest-v3-dialog-dismiss-button',
      }}
    >
      <Stack spacing={2}>
        <Typography variant="body1">
          <Trans i18nKey={descriptionKey} components={{ bold: <strong /> }} />
        </Typography>
        <Link href={HelpCenterUrl.GREENHOUSE_HARVEST_V3_INTEGRATION_STEPS} target="_blank" rel="noopener noreferrer">
          {t('common.greenhouseHarvestV3Dialog.learnMore')}
        </Link>
      </Stack>
    </Dialog>
  );
};

export default GreenhouseHarvestV3Dialog;
